import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMemo, useEffect, useState } from 'react';

import { Flex, Group, Stack } from '@mantine/core';

import { formatSectionsMetadata } from '../../utils/apiUtils';
import useEnsisQuery from '../../hooks/useEnsisQuery';
import { CenteredLoader } from '../../components';
import { PROPOSAL_STATUS_TYPES_PRE_AUTHORING, type ProposalStatusType } from '../../utils/constants';
import EditorHeader from '../../components/EditorHeader';
import EditorSideMenu from '../../components/EditorSideMenu';

const EditorPage: React.FC = () => {
  const { proposalUid, section, organization } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { data, isLoading, isSuccess } = useEnsisQuery(`/app/proposals/${proposalUid}/data`);

  const setupComplete = !PROPOSAL_STATUS_TYPES_PRE_AUTHORING.includes(data?.status as ProposalStatusType);
  const sectionData = useMemo(
    () => (formatSectionsMetadata(data?.sections) ?? []),
    [data, section]
  );
  const [showMenuOnHover, setShowMenuOnHover] = useState(true);

  useEffect(() => {
    if (setupComplete && isSuccess && (pathname === `/org/${organization}/proposals/${proposalUid}` ||
      pathname === `/org/${organization}/proposals/${proposalUid}/`)) {
      navigate(`edit/${sectionData[0].uid}`, { replace: true });
    };
  }, [location.pathname, isSuccess, setupComplete]);
  const [sideMenuLocked, setSideMenuLocked] = useState(true);
  const showSideMenu = sideMenuLocked && pathname.includes('edit');
  if (isLoading || data === undefined) {
    return <CenteredLoader style={{ height: '100vh', width: '100%' }} />;
  }

  return (
    <Group flex={1} gap={0} align='stretch'>
      {showSideMenu &&
        <Flex w={268}>
          <EditorSideMenu
            sections={sectionData}
            p='8px'
            hasEditPermission={true}
            toggleLockedSideMenu={() => { setSideMenuLocked(!sideMenuLocked); setShowMenuOnHover(false); }}
            tooltipLabel='Close outline'
          />
        </Flex>
      }
      <Stack flex={1} gap={0} align='stretch'>
        <EditorHeader
          showMenuOnHover={showMenuOnHover}
          setShowMenuOnHover={setShowMenuOnHover}
          sideMenuLocked={sideMenuLocked}
          setSideMenuLocked={setSideMenuLocked}
          sections={sectionData}
          proposal={data}
        />
        <Outlet />
      </Stack >
    </Group>
  );
};

export default EditorPage;
